import { makeAutoObservable, runInAction } from 'mobx';
import createStoreContext from '../createStoreContext';
import { SpaceLookupItem } from 'src/types/spacesTypes';
import {
  fetchOrganizationMemorySpaceLookupData,
  fetchOrganizationMemorySpaceLookupDataAsSuperAdmin,
  fetchOrganizationSpaceLookupData,
  fetchOrganizationSpaceLookupDataAsSuperAdmin,
} from 'src/services/spaces';
import {
  SPACE_LOOKUP_DEFAULT_LIMIT,
  SPACE_LOOKUP_DEFAULT_PAGE,
} from 'src/constants/spaces';
import { ZERO } from 'src/constants/utils';
import { TASK_SPACES_CACHE_TTL_MS } from 'src/constants/taskCache';
import type {
  ApiResponse,
  PaginatedResponse,
} from 'src/types/commonResponseTypes';

interface FetchOrganizationSpacesLookupParams {
  page?: number;
  limit?: number;
  search?: string;
  targetUserId?: string;
}

export const [SpaceStoreProvider, useSpaceStore] = createStoreContext(
  class SpaceStore {
    organizationSpacesLookup: SpaceLookupItem[] = [];
    organizationSpacesLookupTotalCount = ZERO;
    isOrganizationSpacesLookupLoading = false;
    organizationMemorySpacesLookup: SpaceLookupItem[] = [];
    organizationMemorySpacesLookupTotalCount = ZERO;
    isOrganizationMemorySpacesLookupLoading = false;
    private organizationSpacesLookupRequestId = ZERO;
    private organizationMemorySpacesLookupRequestId = ZERO;
    organizationSpacesLookupCacheByTaskId: Record<
      string,
      { items: SpaceLookupItem[]; totalCount: number; lastFetchedAt: number }
    > = {};

    constructor() {
      makeAutoObservable(this);
    }

    private isCacheFresh(lastFetchedAt: number, ttlMs: number): boolean {
      return Date.now() - lastFetchedAt < ttlMs;
    }

    fetchOrganizationSpacesLookup = async (
      params: FetchOrganizationSpacesLookupParams = {},
    ) => {
      const page = params.page ?? SPACE_LOOKUP_DEFAULT_PAGE;
      const limit = params.limit ?? SPACE_LOOKUP_DEFAULT_LIMIT;
      const requestId = ++this.organizationSpacesLookupRequestId;

      runInAction(() => {
        this.isOrganizationSpacesLookupLoading = true;
      });

      const response = params.targetUserId
        ? await fetchOrganizationSpaceLookupDataAsSuperAdmin(
            params.targetUserId,
            page,
            limit,
            params.search,
          )
        : await fetchOrganizationSpaceLookupData(page, limit, params.search);

      runInAction(() => {
        // A newer query (including a cached task lookup) owns the visible list.
        if (requestId !== this.organizationSpacesLookupRequestId) return;
        this.isOrganizationSpacesLookupLoading = false;

        if (response.success && response.content) {
          this.organizationSpacesLookup = response.content.data;
          this.organizationSpacesLookupTotalCount = response.content.totalCount;
        } else {
          this.organizationSpacesLookup = [];
          this.organizationSpacesLookupTotalCount = ZERO;
        }
      });

      return response;
    };

    fetchOrganizationMemorySpacesLookup = async (
      params: FetchOrganizationSpacesLookupParams = {},
    ) => {
      const page = params.page ?? SPACE_LOOKUP_DEFAULT_PAGE;
      const limit = params.limit ?? SPACE_LOOKUP_DEFAULT_LIMIT;
      const requestId = ++this.organizationMemorySpacesLookupRequestId;

      runInAction(() => {
        this.isOrganizationMemorySpacesLookupLoading = true;
      });

      const response = params.targetUserId
        ? await fetchOrganizationMemorySpaceLookupDataAsSuperAdmin(
            params.targetUserId,
            page,
            limit,
            params.search,
          )
        : await fetchOrganizationMemorySpaceLookupData(
            page,
            limit,
            params.search,
          );

      runInAction(() => {
        if (requestId !== this.organizationMemorySpacesLookupRequestId) {
          return;
        }

        this.isOrganizationMemorySpacesLookupLoading = false;

        if (response.success && response.content) {
          this.organizationMemorySpacesLookup = response.content.data;
          this.organizationMemorySpacesLookupTotalCount =
            response.content.totalCount;
        } else {
          this.organizationMemorySpacesLookup = [];
          this.organizationMemorySpacesLookupTotalCount = ZERO;
        }
      });

      return response;
    };

    fetchOrganizationSpacesLookupForTask = async (
      taskId: string,
      params: FetchOrganizationSpacesLookupParams = {},
      options: { force?: boolean } = {},
    ): Promise<ApiResponse<PaginatedResponse<SpaceLookupItem[]>>> => {
      const page = params.page ?? SPACE_LOOKUP_DEFAULT_PAGE;
      const limit = params.limit ?? SPACE_LOOKUP_DEFAULT_LIMIT;
      const isSearch = Boolean(params.search);
      const isTargetUserLookup = Boolean(params.targetUserId);
      const cached = this.organizationSpacesLookupCacheByTaskId[taskId];
      const requestId = ++this.organizationSpacesLookupRequestId;

      if (
        cached &&
        !isSearch &&
        !isTargetUserLookup &&
        !options.force &&
        this.isCacheFresh(cached.lastFetchedAt, TASK_SPACES_CACHE_TTL_MS)
      ) {
        // Serve cached space lists for a task unless stale or explicitly refreshed.
        runInAction(() => {
          this.organizationSpacesLookup = cached.items;
          this.organizationSpacesLookupTotalCount = cached.totalCount;
          this.isOrganizationSpacesLookupLoading = false;
        });

        return {
          success: true,
          content: {
            data: cached.items,
            totalCount: cached.totalCount,
          },
        };
      }

      runInAction(() => {
        this.isOrganizationSpacesLookupLoading = true;
      });

      const response = params.targetUserId
        ? await fetchOrganizationSpaceLookupDataAsSuperAdmin(
            params.targetUserId,
            page,
            limit,
            params.search,
          )
        : await fetchOrganizationSpaceLookupData(page, limit, params.search);

      runInAction(() => {
        // A newer query (including a cached task lookup) owns the visible list.
        if (requestId !== this.organizationSpacesLookupRequestId) return;
        this.isOrganizationSpacesLookupLoading = false;

        if (response.success && response.content) {
          this.organizationSpacesLookup = response.content.data;
          this.organizationSpacesLookupTotalCount = response.content.totalCount;

          if (!isSearch && !isTargetUserLookup) {
            this.organizationSpacesLookupCacheByTaskId[taskId] = {
              items: response.content.data,
              totalCount: response.content.totalCount,
              lastFetchedAt: Date.now(),
            };
          }
        } else if (cached && !isSearch && !isTargetUserLookup) {
          this.organizationSpacesLookup = cached.items;
          this.organizationSpacesLookupTotalCount = cached.totalCount;
        } else {
          this.organizationSpacesLookup = [];
          this.organizationSpacesLookupTotalCount = ZERO;
        }
      });

      return response;
    };
  },
);
